import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  Chip,
  Checkbox,
  Typography,
  Box,
  Tabs,
  Tab,
  Pagination,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { Check, Clear, Visibility } from "@mui/icons-material";
import classes from "./approveComponent.module.css";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAdminProfiles,
  approveUsers,
} from "../../redux/slices/adminSlice";

const ApproveComponent = () => {
  // State variables
  const [loading, setLoading] = useState(false);
  const [selectedProfiles, setSelectedProfiles] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [page, setPage] = useState(1);
  const [profilesPerPage] = useState(10);
  const [viewMode, setViewMode] = useState("pending"); // "all" or "pending"
  const [selectedCategory, setSelectedCategory] = useState(""); // Added state for category filter

  // Available categories
  const categories = [
    "singer",
    "musician",
    "dancer",
    "comedian",
    "magician",
    "music_band",
    "dance_group",
    "dj",
    "rapper",
    "beat_boxer",
    "video_editor",
    "speaker",
    "photography",
  ];

  const dispatch = useDispatch();
  const {
    adminProfiles,
    totalProfiles,
    loading: profilesLoading,
  } = useSelector((state) => state.admin);

  // Calculate total pages based on totalProfiles
  const totalPages = Math.ceil(totalProfiles / profilesPerPage) || 1;

  // Fetch profiles based on the viewMode, page and category
  const fetchProfiles = () => {
    setLoading(true);
    dispatch(
      fetchAdminProfiles({
        page_number: page,
        page_size: profilesPerPage,
        sortBy: "created_at:desc",
        type: viewMode === "pending" ? "artist" : null,
        approved: viewMode === "pending" ? false : undefined,
        category: selectedCategory || undefined, // Add category filter when selected
      })
    );
    setLoading(false);
  };

  // Handle approval of selected profiles
  const handleApproveSelected = async () => {
    if (selectedProfiles.length === 0) return;

    setLoading(true);
    try {
      // Use the correct dispatch action for approval
      await dispatch(approveUsers({ ids: selectedProfiles }));
      // Refresh profiles after approval
      fetchProfiles();
      // Clear selection
      setSelectedProfiles([]);
    } catch (error) {
      console.error("Error approving profiles:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setViewMode(newValue === 0 ? "pending" : "all");
    setPage(1);
    setSelectedProfiles([]);
  };

  // Handle page change
  const handlePageChange = (event, value) => {
    setPage(value);
  };

  // Handle selection of profiles
  const handleSelectProfile = (profileId) => {
    setSelectedProfiles((prev) => {
      if (prev.includes(profileId)) {
        return prev.filter((id) => id !== profileId);
      } else {
        return [...prev, profileId];
      }
    });
  };

  // Handle select all profiles on current page
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const currentIds =
        adminProfiles.results?.map((profile) => profile.id) || [];
      setSelectedProfiles(currentIds);
    } else {
      setSelectedProfiles([]);
    }
  };

  // Handle category filter change
  const handleCategoryChange = (event) => {
    setSelectedCategory(event.target.value);
    setPage(1); // Reset to first page when changing category
    setSelectedProfiles([]); // Clear selections when changing filter
  };

  // Load profiles when component mounts or dependencies change
  useEffect(() => {
    fetchProfiles();
  }, [page, viewMode, selectedCategory]); // Added selectedCategory as dependency

  // Check if all profiles on current page are selected
  const isAllSelected =
    adminProfiles?.results?.length > 0 &&
    adminProfiles.results.every((profile) =>
      selectedProfiles.includes(profile.id)
    );

  return (
    <div className={classes.adminContainer}>
      <div className={classes.headerSection}>
        <Typography variant="h4" component="h1" className={classes.title}>
          Artist Profile Approval
        </Typography>
        <Box className={classes.actionButtons}>
          <button
            className={classes.approveButton}
            onClick={handleApproveSelected}
            disabled={selectedProfiles.length === 0 || loading}
          >
            Approve Selected ({selectedProfiles.length})
          </button>
        </Box>
      </div>

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          className={classes.tabs}
          sx={{ color: "white" }}
        >
          <Tab
            label="Pending Approval"
            className={classes.tab}
            sx={{ color: "white" }}
          />
          <Tab
            label="All Profiles"
            className={classes.tab}
            sx={{ color: "white" }}
          />
        </Tabs>
      </Box>

      {/* Category Filter */}
      <Box sx={{ mb: 2, maxWidth: 300 }}>
        <FormControl
          fullWidth
          variant="outlined"
          className={classes.categoryFilter}
          sx={{
            backgroundColor: "white",
            "& .MuiOutlinedInput-root": {
              backgroundColor: "white",
              "& fieldset": {
                borderColor: "transparent", // Light gray border
              },
              "&:hover fieldset": {
                borderColor: "#999", // Slightly darker on hover
              },
              "&.Mui-focused fieldset": {
                borderColor: "#ccc", // Keep same color on focus
              },
            },
          }}
        >
          <InputLabel id="category-select-label">Filter by Category</InputLabel>
          <Select
            labelId="category-select-label"
            id="category-select"
            value={selectedCategory}
            onChange={handleCategoryChange}
          >
            <MenuItem value="">
              <em>All Categories</em>
            </MenuItem>
            {categories.map((category) => (
              <MenuItem key={category} value={category}>
                {category
                  .replace(/_/g, " ")
                  .replace(/\b\w/g, (c) => c.toUpperCase())}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {loading || profilesLoading ? (
        <div className={classes.loadingContainer}>
          <CircularProgress />
        </div>
      ) : (
        <>
          <TableContainer component={Paper} className={classes.tableContainer}>
            <Table>
              <TableHead className={classes.tableHead}>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={isAllSelected}
                      onChange={handleSelectAll}
                      indeterminate={
                        selectedProfiles.length > 0 && !isAllSelected
                      }
                    />
                  </TableCell>
                  <TableCell>Profile</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Username</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Categories</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {adminProfiles?.results?.length > 0 ? (
                  adminProfiles.results.map((profile) => (
                    <TableRow
                      key={profile.id}
                      className={classes.tableRow}
                      selected={selectedProfiles.includes(profile.id)}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedProfiles.includes(profile.id)}
                          onChange={() => handleSelectProfile(profile.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Avatar
                          src={profile.displayPictureUrl || ""}
                          alt={profile.first_name}
                          className={classes.avatar}
                        />
                      </TableCell>
                      <TableCell>
                        {profile.first_name} {profile.last_name}
                      </TableCell>
                      <TableCell>{profile.username}</TableCell>
                      <TableCell>
                        <Chip
                          label={profile.type}
                          size="small"
                          className={classes.typeChip}
                        />
                      </TableCell>
                      <TableCell>
                        {profile.categories
                          ?.map((cat) => cat.name)
                          .join(", ") || "N/A"}
                      </TableCell>
                      <TableCell>
                        {profile.available_locations?.join(", ") || "N/A"}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={profile.approved ? "Approved" : "Pending"}
                          color={profile.approved ? "success" : "warning"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <div className={classes.actionButtons}>
                          <Button
                            variant="outlined"
                            size="small"
                            className={classes.viewButton}
                            startIcon={<Visibility />}
                            onClick={() =>
                              window.open(
                                `/profile/${profile?.username}`,
                                "_blank"
                              )
                            }
                          >
                            View
                          </Button>
                          {!profile.approved && (
                            <Button
                              variant="contained"
                              size="small"
                              color="primary"
                              className={classes.approveIndividualButton}
                              startIcon={<Check />}
                              onClick={() => {
                                setSelectedProfiles([profile.id]);
                                handleApproveSelected();
                              }}
                            >
                              Approve
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={9}
                      align="center"
                      className={classes.noDataCell}
                    >
                      No profiles to display
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box className={classes.paginationContainer}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              className={classes.pagination}
            />
          </Box>
        </>
      )}
    </div>
  );
};

export default ApproveComponent;
